<script setup lang="ts">
import type {
  FastCheckoutMethod,
  FastCheckoutSurface,
} from '@design-system/composables/useFastCheckoutMethods'
import IconsApplePayFast from '~icons/icons/ApplePayFast.svg'
import IconsGooglePayFastColor from '~icons/icons/GooglePayFastColor.svg'
import IconsPayPalFastColor from '~icons/icons/PayPalFastColor.svg'

const METHOD_LABEL: Record<FastCheckoutMethod, string> = {
  applePay: 'Apple Pay',
  googlePay: 'Google Pay',
  payPal: 'PayPal',
}

const METHOD_ICON: Record<FastCheckoutMethod, typeof IconsApplePayFast> = {
  applePay: IconsApplePayFast,
  googlePay: IconsGooglePayFastColor,
  payPal: IconsPayPalFastColor,
}

const props = withDefaults(
  defineProps<{
    method: FastCheckoutMethod
    surface?: FastCheckoutSurface
    compact?: boolean
    loading?: boolean
    disabled?: boolean
  }>(),
  {
    surface: 'pdp',
    compact: false,
  }
)

const emit = defineEmits<{
  click: []
}>()

const isAppleOnLayer = computed(
  () => props.surface === 'layer' && props.method === 'applePay'
)

const showBuyWith = computed(() => !props.compact && props.method !== 'payPal')

const handleClick = () => {
  if (props.loading || props.disabled) return
  emit('click')
}
</script>

<template>
  <button
    type="button"
    class="flex h-12 w-full items-center justify-center"
    :class="{
      'bg-primitives-black': isAppleOnLayer,
      'border-primitives-black bg-primitives-white border': !isAppleOnLayer,
      'pointer-events-none opacity-50': loading || disabled,
    }"
    :aria-label="METHOD_LABEL[method]"
    :aria-busy="loading || undefined"
    :disabled="loading || disabled"
    @click="handleClick"
  >
    <span
      v-if="showBuyWith"
      class="text-book-6 mr-2"
      :class="
        isAppleOnLayer ? 'text-primitives-white' : 'text-primitives-black'
      "
    >
      Buy with
    </span>
    <component
      :is="METHOD_ICON[method]"
      class="w-auto"
      :class="{
        'h-5': method === 'applePay',
        'h-4': method !== 'applePay',
        'brightness-0 invert': isAppleOnLayer,
      }"
      aria-hidden="true"
    />
  </button>
</template>
